import { useState } from 'react';
import { Descriptions, Flex, Segmented, Typography } from 'antd';
import { CounterpartySelect, useListCounterpartiesQuery } from '@/entities/counterparty';
import { useLazyExportSalesProfitXlsxQuery, useSalesProfitReportQuery } from '@/entities/reports';
import type {
  SalesProfitGroupBy,
  SalesProfitMeasures,
  SalesProfitRow,
  SalesProfitRowCategory,
  SalesProfitRowCounterparty,
  SalesProfitRowDay,
  SalesProfitRowDocument,
} from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';
import { displaySigned, formatMoney } from '@/shared/lib/money';
import { PeriodFilter } from './PeriodFilter';
import { presetRange } from './periodPresets';

const DEFAULT_GROUP: SalesProfitGroupBy = 'counterparty';

const GROUP_OPTIONS: { value: SalesProfitGroupBy; label: string }[] = [
  { value: 'counterparty', label: 'По контрагентам' },
  { value: 'category', label: 'По товарам' },
  { value: 'document', label: 'По документам' },
  { value: 'day', label: 'По дням' },
];

const PRELIMINARY_HINT = 'Себестоимость предварительная — прибыль может измениться';

const money = (amount: string) => displaySigned(formatMoney(amount));
const margin = (marginPct: string | null) => (marginPct === null ? '—' : `${marginPct}%`);

/** Прибыль строки: убыток — красным, «≈» — себестоимость ещё может измениться. */
function ProfitCell({ profit, isPreliminary }: Pick<SalesProfitMeasures, 'profit' | 'isPreliminary'>) {
  return (
    <Typography.Text strong type={profit.startsWith('-') ? 'danger' : undefined} title={isPreliminary ? PRELIMINARY_HINT : undefined}>
      {isPreliminary ? '≈ ' : ''}
      {money(profit)}
    </Typography.Text>
  );
}

/** Четыре столбца, общие для всех разрезов. */
function measureColumns<T extends SalesProfitMeasures>(): ResponsiveColumns<T> {
  return [
    { title: 'Выручка', dataIndex: 'revenue', width: 120, align: 'right', render: money },
    { title: 'Себестоимость', dataIndex: 'cost', width: 130, align: 'right', render: money },
    // Телефон: прибыль — справа в заголовке карточки, остальное — строками.
    { title: 'Прибыль', dataIndex: 'profit', width: 120, align: 'right', mobile: 'badge', render: (_: string, row: T) => <ProfitCell profit={row.profit} isPreliminary={row.isPreliminary} /> },
    { title: 'Маржа', dataIndex: 'marginPct', width: 80, align: 'right', render: margin },
  ];
}

const COUNTERPARTY_COLUMNS: ResponsiveColumns<SalesProfitRowCounterparty> = [
  { title: 'Контрагент', dataIndex: 'counterpartyName', render: (v: string | null) => v ?? '—' },
  { title: 'Документов', dataIndex: 'docCount', width: 100, align: 'right' },
  ...measureColumns<SalesProfitRowCounterparty>(),
];
const CATEGORY_COLUMNS: ResponsiveColumns<SalesProfitRowCategory> = [
  { title: 'Товар', dataIndex: 'categoryName' },
  { title: 'Штук', dataIndex: 'qty', width: 90, align: 'right' },
  ...measureColumns<SalesProfitRowCategory>(),
];
const DOCUMENT_COLUMNS: ResponsiveColumns<SalesProfitRowDocument> = [
  { title: 'Номер', dataIndex: 'number', width: 100 },
  { title: 'Тип', dataIndex: 'typeLabel', width: 150, mobile: 'subtitle' },
  { title: 'Дата', dataIndex: 'docDate', width: 100, mobile: 'subtitle', render: (v: string) => formatDate(v) },
  { title: 'Контрагент', dataIndex: 'counterpartyName', render: (v: string | null) => v ?? '—' },
  ...measureColumns<SalesProfitRowDocument>(),
];
const DAY_COLUMNS: ResponsiveColumns<SalesProfitRowDay> = [
  { title: 'Дата', dataIndex: 'docDate', width: 110, render: (v: string) => formatDate(v) },
  { title: 'Документов', dataIndex: 'docCount', width: 100, align: 'right' },
  ...measureColumns<SalesProfitRowDay>(),
];

const COLUMNS_BY_GROUP: Record<SalesProfitGroupBy, ResponsiveColumns<SalesProfitRow>> = {
  counterparty: COUNTERPARTY_COLUMNS as ResponsiveColumns<SalesProfitRow>,
  category: CATEGORY_COLUMNS as ResponsiveColumns<SalesProfitRow>,
  document: DOCUMENT_COLUMNS as ResponsiveColumns<SalesProfitRow>,
  day: DAY_COLUMNS as ResponsiveColumns<SalesProfitRow>,
};

function rowKeyFor(groupBy: SalesProfitGroupBy, row: SalesProfitRow): string {
  if (groupBy === 'document') return (row as SalesProfitRowDocument).documentId;
  if (groupBy === 'category') return (row as SalesProfitRowCategory).categoryId;
  if (groupBy === 'counterparty') return (row as SalesProfitRowCounterparty).counterpartyId ?? 'none';
  return (row as SalesProfitRowDay).docDate;
}

/** §5, §11.5: «Прибыль по продажам» — сколько заработано на проданном (выручка − себестоимость, возвраты
 *  от клиентов вычтены) за период, по всем контрагентам или по одному, в разрезе контрагентов / товаров /
 *  документов / дней. Итог равен «чистые продажи − чистая себестоимость» отчёта «Финансовые отчёты». */
export function SalesProfitReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultPeriod = presetRange('month', today);
  const [from, setFrom] = useState(defaultPeriod.from);
  const [to, setTo] = useState(defaultPeriod.to);
  const [counterpartyId, setCounterpartyId] = useState<string>();
  const [groupBy, setGroupBy] = useState<SalesProfitGroupBy>(DEFAULT_GROUP);
  const periodChanged = from !== defaultPeriod.from || to !== defaultPeriod.to;
  const params = { from, to, counterpartyId, groupBy };
  const { data, isFetching } = useSalesProfitReportQuery(params, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportSalesProfitXlsxQuery();
  // Тот же запрос, что делает поле выбора, — из кэша; нужен только ради названия в сводке фильтров.
  const { data: counterparties = [] } = useListCounterpartiesQuery({});
  const counterpartyName = counterpartyId ? counterparties.find((c) => c.id === counterpartyId)?.name : undefined;
  const groupLabel = GROUP_OPTIONS.find((o) => o.value === groupBy)?.label ?? '';
  const totals = data?.totals;

  return (
    <ReportPage
      title="Прибыль по продажам"
      filters={
        <Flex vertical={isMobile} wrap={!isMobile} gap={isMobile ? 12 : 8} align={isMobile ? 'stretch' : 'center'}>
          <PeriodFilter
            from={from}
            to={to}
            today={today}
            onChange={(range) => {
              setFrom(range.from);
              setTo(range.to);
            }}
          />
          <div style={isMobile ? undefined : { width: 260 }}>
            <CounterpartySelect value={counterpartyId} onChange={setCounterpartyId} placeholder="Все контрагенты" />
          </div>
          <Segmented value={groupBy} onChange={(v) => setGroupBy(v)} options={GROUP_OPTIONS} vertical={isMobile} />
        </Flex>
      }
      summary={
        totals ? (
          <Flex vertical gap={4}>
            <Descriptions size="small" bordered column={isMobile ? 2 : 4}>
              <Descriptions.Item label="Выручка">{money(totals.revenue)}</Descriptions.Item>
              <Descriptions.Item label="Себестоимость">{money(totals.cost)}</Descriptions.Item>
              <Descriptions.Item label="Прибыль">
                <ProfitCell profit={totals.profit} isPreliminary={totals.isPreliminary} />
              </Descriptions.Item>
              <Descriptions.Item label="Маржа">{margin(totals.marginPct)}</Descriptions.Item>
            </Descriptions>
            {totals.isPreliminary ? <Typography.Text type="warning">{PRELIMINARY_HINT}.</Typography.Text> : null}
          </Flex>
        ) : null
      }
      columns={COLUMNS_BY_GROUP[groupBy]}
      activeFilters={(periodChanged ? 1 : 0) + (counterpartyId ? 1 : 0) + (groupBy !== DEFAULT_GROUP ? 1 : 0)}
      onResetFilters={() => {
        setFrom(defaultPeriod.from);
        setTo(defaultPeriod.to);
        setCounterpartyId(undefined);
        setGroupBy(DEFAULT_GROUP);
      }}
      filtersSummary={`${formatDate(from)} — ${formatDate(to)}${counterpartyName ? ` · ${counterpartyName}` : ''} · ${groupLabel}`}
      data={data?.rows ?? []}
      loading={isFetching}
      rowKey={(r) => rowKeyFor(groupBy, r)}
      onExport={() => exportXlsx(params).unwrap()}
      exportFilename="Прибыль по продажам.xlsx"
    />
  );
}
